import values from 'lodash/values';
import moment from 'moment';
import get from 'lodash/get';
import find from 'lodash/find';
import pick from 'lodash/pick';
import orderBy from 'lodash/orderBy';
import compact from 'lodash/compact';
import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';

const defaultInitialState = {
	columns: {
		date: {
			index: 'date',
			title: 'Date',
			direction: 'desc'
		},
		id: {
			index: 'id',
			title: 'ID',
			direction: 'desc'
		},
		offer: {
			index: 'offer',
			title: 'Offer'
		},
		ip: {
			index: 'ip',
			title: 'Ip'
		},
		geo: {
			index: 'geo',
			title: 'Geo',
			direction: 'desc'
		},
		status: {
			index: 'status',
			title: 'Status',
			direction: 'desc'
		},
		payout: {
			index: 'payout',
			title: 'Payout',
			direction: 'desc'
		},
		affiliates: {
			index: 'affiliate',
			title: 'Affiliate'
		},
		terminal: {
			index: 'terminal',
			title: 'non term'
		},
		'traffic_type': {
			index: 'traffic_type',
			title: 'Traffic type',
			direction: 'desc'
		},
		'actions': {
			title: 'Detail'
		}
	},
	sortByColumn: {
		index: 'date',
		title: 'Date',
		direction: 'desc'
	},
	pagination: {
		rowsNumber: 20,
		rowsPerPage: 20
	},
	conversionExtendedData: [],
	detailsRowId: null
};

const createTable = ({
	namespace,
	stateNodeName
}) => {
	// action types
	const SORT_BY_COLUMN = `${namespace}/table-sort-by-column`;
	const SHOW_MORE_ROWS = `${namespace}/table-show-more-rows`;
	const FETCH_CONVERSION_DATA_SUCCESS = `${namespace}/FETCH_CONVERSION_DATA_SUCCESS`;
	const FETCH_CONVERSION_DATA_ERROR = `${namespace}/FETCH_CONVERSION_DATA_ERROR`;

	const TOGGLE_DETAILS = `${namespace}/toggle-details`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		showMoreRows: () => ({
			type: SHOW_MORE_ROWS
		}),

		toggleDetails: rowId => dispatch => {
			return http({
				url: `${endpoints.STATISTICS.CONVERSIONS}/${rowId}`,
				method: 'get',
				actions: [
					FETCH_CONVERSION_DATA_SUCCESS,
					FETCH_CONVERSION_DATA_ERROR
				]
			})(dispatch)
				.then(action => {
					if (action.type === FETCH_CONVERSION_DATA_SUCCESS) {
						return dispatch({
							type: TOGGLE_DETAILS,
							payload: rowId
						});
					}
				});
		}
	};

	// reducers
	const reducers = {
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns }  = state.table;
			const columnIndex = action.payload;
			const direction = sortByColumn['index'] === columnIndex ?
				sortByColumn.direction === 'desc' ? 'asc' : 'desc' :
				columns[columnIndex].direction;

			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[FETCH_CONVERSION_DATA_SUCCESS]: (state, action) => {

			return {
				...state,
				table: {
					...state.table,
					conversionExtendedData: [...state.table.conversionExtendedData, action.payload.data]
				}
			};
		},

		[SHOW_MORE_ROWS]: state => ({
			...state,
			table: {
				...state.table,
				pagination: {
					...state.table.pagination,
					rowsNumber: state.table.pagination.rowsNumber += state.table.pagination.rowsPerPage
				}
			}
		}),

		[TOGGLE_DETAILS]: (state, action) => {
			const detailsRowId = action.payload;
			return {
				...state,
				table: {
					...state.table,
					detailsRowId: state.table.detailsRowId === detailsRowId ? null : detailsRowId
				}
			};
		}
	};

	// selectors
	const selectors = {
		getTable: (state, usePagination) => {
			const { table, data, searchCrmIdText, searchCpaIdText } = state[namespace];
			const rows = data ? sortColumns(data, table.sortByColumn) : [];
			const lists = state.root.lists;

			let modifyRows = rows.map(row => ({
				...row,
				sid: getSids(row),
				geo: `${row.country || '-'}, ${row.city || '-'}`,
				status: getTextFromList(lists['conversion-statuses'], row['status']),
				traffic_type: getTextFromList(lists['traffic-types'], row['traffic_type'])
			}));

			if (searchCrmIdText.length) {
				modifyRows = modifyRows.filter((row) => textToList(searchCrmIdText).includes(row.crm_id));
			}
			if (searchCpaIdText.length) {
				modifyRows = modifyRows.filter((row) => textToList(searchCpaIdText).includes(String(row.id)));
			}

			return {
				headers: values(table.columns),
				rows: usePagination ? modifyRows : modifyRows.slice(0, table.pagination.rowsNumber),
				sortedColumn: table.sortByColumn,
				rowsNumber: table.pagination.rowsNumber,
				conversionExtendedData: table.conversionExtendedData,
				totalRows: rows.length,
				detailsRowId: table.detailsRowId,
				shouldShowPaginationControl: modifyRows.length > table.pagination.rowsNumber
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(rows, row => {
		const value = row[sortedColumn.index];
		switch (sortedColumn.index) {
			case 'date':
				return  moment(value).valueOf();
			case 'payout':
				return parseFloat(value);
			default:
				return value;
		}
	}, sortedColumn.direction);
};

const getTextFromList = (lists, id) => get(find(lists, { id }), 'text');
const getSids = row => {
	const sids = ['sid1', 'sid2', 'sid3', 'sid4', 'sid5'];
	return (compact(
		values(
			pick(row, sids)
		)
	)).join(':');
};

const textToList = (string) => string.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().split(' ');

export default createTable;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/statistics/components/table/conversions/index.js